import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Play, ShoppingCart, Sparkles } from "lucide-react";
import { MARKETPLACE_AGENTS } from "@/data/agents";
import AgentChat from "@/components/AgentChat";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import { toast } from "sonner";

const Marketplace = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [activeCategory, setActiveCategory] = useState("all");
  const { language } = useLanguage();

  const categories = ["all", ...new Set(MARKETPLACE_AGENTS.map(a => a.category.toLowerCase()))];

  const filteredAgents = MARKETPLACE_AGENTS.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === "all" || agent.category.toLowerCase() === activeCategory;
    return matchesSearch && matchesCategory;
  });

  if (selectedAgent) {
    return <AgentChat agent={selectedAgent} onBack={() => setSelectedAgent(null)} />;
  }

  return (
    <div className="space-y-6" data-testid="marketplace-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{translate("Agent Marketplace", language)}</h1>
          <p className="text-gray-400">{translate("Deploy specialized humanoid agents for real-world tasks", language)}</p>
        </div>
        <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
          <ShoppingCart className="w-3 h-3 mr-1" />
          {MARKETPLACE_AGENTS.length} {translate("Agents Available", language)}
        </Badge>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search marketplace agents..."
          className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
        />
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={setActiveCategory}>
        <TabsList className="bg-[#0A0A0A] border border-white/10">
          {categories.map(cat => (
            <TabsTrigger
              key={cat}
              value={cat}
              className="data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-400"
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Agent Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAgents.map((agent) => (
          <Card
            key={agent.id}
            className="bg-[#0A0A0A] border-white/10 hover:border-purple-500/30 transition-all group cursor-pointer"
            onClick={() => setSelectedAgent(agent)}
          >
            <CardContent className="p-6">
              {/* Icon and Status */}
              <div className="flex items-start justify-between mb-4">
                <div className="text-4xl">{agent.icon}</div>
                <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">
                  {agent.status === "production" ? "✓ Production" : "⚡ Experimental"}
                </Badge>
              </div>

              {/* Name and Tier */}
              <div className="mb-3">
                <h3 className="text-lg font-bold text-white mb-1 group-hover:text-purple-400 transition-colors">
                  {translate(agent.name, language)}
                </h3>
                <Badge className="bg-gray-500/10 text-gray-400 border-gray-500/20 text-xs">
                  {agent.tier}
                </Badge>
              </div>

              {/* Description */}
              <p className="text-sm text-gray-400 mb-4 line-clamp-3">
                {translate(agent.retailExplanation, language)}
              </p>

              {/* Status */}
              <div className="flex items-center justify-between text-xs mb-4">
                <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                  {agent.status === "production" ? "✓ Production" : "⚡ Experimental"}
                </Badge>
                <span className="text-gray-500">{agent.version}</span>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  className="flex-1 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 border border-purple-500/20 hover:border-purple-500/40"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedAgent(agent);
                  }}
                >
                  <Play className="w-4 h-4 mr-2" />
                  {translate("Try Agent", language)}
                </Button>
                <Button
                  variant="outline"
                  className="border-white/10 hover:bg-white/5 text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    toast.success(`${agent.name} added to My Robots`);
                  }}
                >
                  <ShoppingCart className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredAgents.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No agents found</p>
        </div>
      )}
    </div>
  );
};

export default Marketplace;
